import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from './api';
import { useRealtime } from './realtime';

export interface PhoneCall {
  ref: string;
  direction: 'in' | 'out';
  number: string;
  name: string;
  /** `ringing`: klingelt (eingehend bei uns, ausgehend beim Ziel), `connecting`: Verbindung wird aufgebaut. */
  state: 'ringing' | 'connecting' | 'active' | 'ended';
  held: boolean;
  muted: boolean;
  answeredAt: number | null;
  note: string;
  /** Angaben des Anrufers aus dem Sekretariat. */
  inputs: { name: string; value: string }[];
  queue: string;
}

export type PhoneStatus = 'none' | 'off' | 'connecting' | 'ready' | 'error';

export interface Softphone {
  status: PhoneStatus;
  extension: { number: string; name: string } | null;
  error: string;
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  calls: PhoneCall[];
  dial: (number: string) => Promise<void>;
  accept: (ref: string) => Promise<void>;
  decline: (ref: string) => void;
  hangup: (ref: string) => void;
  toggleHold: (ref: string) => void;
  toggleMute: (ref: string) => void;
  sendDtmf: (ref: string, digits: string) => void;
  transfer: (ref: string, target: string) => void;
  transferTo: (ref: string, withRef: string) => void;
}

const Ctx = createContext<Softphone | null>(null);

interface Resources {
  pc: RTCPeerConnection;
  stream: MediaStream;
  audio: HTMLAudioElement;
}

const STORAGE_KEY = 'voice.softphone';

function readEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

async function gathered(pc: RTCPeerConnection, timeoutMs = 2500): Promise<void> {
  if (pc.iceGatheringState === 'complete') return;
  await new Promise<void>((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check);
      clearTimeout(timer);
      resolve();
    };
    const check = () => pc.iceGatheringState === 'complete' && done();
    const timer = setTimeout(done, timeoutMs);
    pc.addEventListener('icegatheringstatechange', check);
  });
}

/** Klingelton aus zwei Sinustönen, ohne Audiodatei. */
class Ringer {
  private ctx: AudioContext | null = null;
  private timer: ReturnType<typeof setInterval> | undefined;

  start(): void {
    if (this.timer) return;
    try {
      this.ctx ??= new AudioContext();
      void this.ctx.resume();
    } catch {
      return;
    }
    const burst = () => {
      const ctx = this.ctx;
      if (!ctx || ctx.state !== 'running') return;
      const gain = ctx.createGain();
      gain.gain.value = 0.08;
      gain.connect(ctx.destination);
      for (const f of [440, 480]) {
        const osc = ctx.createOscillator();
        osc.frequency.value = f;
        osc.connect(gain);
        osc.start();
        osc.stop(ctx.currentTime + 1.2);
      }
    };
    burst();
    this.timer = setInterval(burst, 3200);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }
}

export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const rt = useRealtime();
  const mine = useQuery({ queryKey: ['me-extension'], queryFn: () => api<{ extensions: { id: string; number: string; displayName: string }[] }>('/me/extension') });
  const hasExtension = (mine.data?.extensions.length ?? 0) > 0;

  const [enabled, setEnabledState] = useState(readEnabled);
  const [status, setStatus] = useState<PhoneStatus>('off');
  const [extension, setExtension] = useState<Softphone['extension']>(null);
  const [error, setError] = useState('');
  const [calls, setCalls] = useState<PhoneCall[]>([]);
  const res = useRef(new Map<string, Resources>());
  const ringer = useRef(new Ringer());
  const callsRef = useRef<PhoneCall[]>([]);
  callsRef.current = calls;

  const patch = useCallback((ref: string, change: Partial<PhoneCall>) => {
    setCalls((list) => list.map((c) => (c.ref === ref ? { ...c, ...change } : c)));
  }, []);

  const release = useCallback((ref: string) => {
    const r = res.current.get(ref);
    if (!r) return;
    res.current.delete(ref);
    r.stream.getTracks().forEach((t) => t.stop());
    r.audio.srcObject = null;
    try {
      r.pc.close();
    } catch {
      /* schon zu */
    }
  }, []);

  const finish = useCallback(
    (ref: string, note: string) => {
      release(ref);
      patch(ref, { state: 'ended', note });
      setTimeout(() => setCalls((list) => list.filter((c) => c.ref !== ref)), 3500);
    },
    [patch, release],
  );

  // Klingelton, solange etwas eingehend klingelt.
  useEffect(() => {
    if (calls.some((c) => c.direction === 'in' && c.state === 'ringing')) ringer.current.start();
    else ringer.current.stop();
  }, [calls]);

  // Telefon beim Server an- und abmelden.
  useEffect(() => {
    if (!hasExtension) {
      setStatus('none');
      return;
    }
    if (!enabled) {
      setStatus('off');
      setExtension(null);
      return;
    }
    if (!rt.connected) {
      setStatus('connecting');
      return;
    }
    setStatus('connecting');
    rt.send({ t: 'phone-on' });
    return () => {
      rt.send({ t: 'phone-off' });
    };
  }, [hasExtension, enabled, rt.connected, rt]);

  // Nachrichten des Servers.
  useEffect(() => {
    return rt.subscribe((msg) => {
      const ref = typeof msg['ref'] === 'string' ? msg['ref'] : '';
      switch (msg['t']) {
        case 'phone-ready': {
          const ext = msg['extension'] as { number: string; name: string };
          setExtension({ number: ext.number, name: ext.name });
          setStatus('ready');
          setError('');
          break;
        }
        case 'phone-error':
          setStatus('error');
          setError(String(msg['message'] ?? ''));
          break;
        case 'incoming': {
          const from = msg['from'] as { number: string; name: string };
          const info = (msg['info'] ?? {}) as { inputs?: { name: string; value: string }[]; queue?: string };
          setCalls((list) => [...list, { ref, direction: 'in', number: from.number, name: from.name, state: 'ringing', held: false, muted: false, answeredAt: null, note: '', inputs: info.inputs ?? [], queue: info.queue ?? '' }]);
          if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
            const n = new Notification('Eingehender Anruf', { body: from.name ? `${from.name} (${from.number})` : from.number, tag: ref });
            setTimeout(() => n.close(), 15_000);
          }
          break;
        }
        case 'cancel':
          finish(ref, 'Anruf verpasst');
          break;
        case 'answer': {
          const r = res.current.get(ref);
          if (r) void r.pc.setRemoteDescription({ type: 'answer', sdp: String(msg['sdp']) }).catch(() => finish(ref, 'Verbindung fehlgeschlagen'));
          break;
        }
        case 'ringing':
          patch(ref, { state: 'ringing', note: 'Es klingelt …' });
          break;
        case 'answered':
          patch(ref, { state: 'active', answeredAt: Date.now(), note: '' });
          break;
        case 'ended': {
          const status = Number(msg['status'] ?? 0);
          const note = status === 486 ? 'Besetzt' : status === 404 ? 'Nummer nicht gefunden' : status === 403 ? 'Nummer gesperrt' : status === 480 || status === 503 ? 'Nicht erreichbar' : status === 603 ? 'Abgelehnt' : 'Gespräch beendet';
          finish(ref, note);
          break;
        }
        case 'transfer':
          patch(ref, { note: msg['final'] ? (Number(msg['status']) < 300 ? 'Weitergeleitet' : 'Weiterleiten fehlgeschlagen') : 'Wird weitergeleitet …' });
          break;
        case 'error':
          if (ref) finish(ref, String(msg['message'] ?? 'Fehler'));
          else setError(String(msg['message'] ?? ''));
          break;
      }
    });
  }, [rt, finish, patch]);

  // Alles aufräumen, wenn die Seite geschlossen wird.
  useEffect(() => {
    const r = res.current;
    return () => {
      for (const ref of [...r.keys()]) release(ref);
    };
  }, [release]);

  const openMedia = useCallback(async (ref: string): Promise<{ pc: RTCPeerConnection; offer: string }> => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
    const pc = new RTCPeerConnection({ iceServers: [] });
    const audio = new Audio();
    audio.autoplay = true;
    pc.ontrack = (e) => {
      audio.srcObject = e.streams[0] ?? new MediaStream([e.track]);
      void audio.play().catch(() => {});
    };
    for (const track of stream.getAudioTracks()) pc.addTrack(track, stream);
    res.current.set(ref, { pc, stream, audio });
    await pc.setLocalDescription(await pc.createOffer());
    await gathered(pc);
    return { pc, offer: pc.localDescription!.sdp };
  }, []);

  const holdOthers = useCallback(
    (except: string) => {
      for (const c of callsRef.current) {
        if (c.ref !== except && c.state === 'active' && !c.held) {
          rt.send({ t: 'hold', ref: c.ref, held: true });
          res.current.get(c.ref)?.stream.getAudioTracks().forEach((t) => (t.enabled = false));
          patch(c.ref, { held: true });
        }
      }
    },
    [rt, patch],
  );

  const fail = useCallback(
    (ref: string, err: unknown) => {
      const denied = err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'NotFoundError');
      finish(ref, denied ? 'Mikrofon nicht verfügbar – bitte Zugriff erlauben' : 'Verbindung fehlgeschlagen');
    },
    [finish],
  );

  const dial = useCallback(
    async (number: string) => {
      const clean = number.trim();
      if (!clean || status !== 'ready') return;
      const ref = crypto.randomUUID();
      holdOthers(ref);
      setCalls((list) => [...list, { ref, direction: 'out', number: clean, name: '', state: 'connecting', held: false, muted: false, answeredAt: null, note: 'Verbindet …', inputs: [], queue: '' }]);
      try {
        const { offer } = await openMedia(ref);
        if (!rt.send({ t: 'call', ref, number: clean, sdp: offer })) throw new Error('offline');
      } catch (err) {
        fail(ref, err);
      }
    },
    [status, holdOthers, openMedia, rt, fail],
  );

  const accept = useCallback(
    async (ref: string) => {
      holdOthers(ref);
      patch(ref, { state: 'connecting', note: 'Verbindet …' });
      try {
        const { offer } = await openMedia(ref);
        if (!rt.send({ t: 'accept', ref, sdp: offer })) throw new Error('offline');
        // „active“ setzt der Server mit der Antwort; bis dahin ist die Verbindung im Aufbau.
        patch(ref, { state: 'active', answeredAt: Date.now(), note: '' });
      } catch (err) {
        rt.send({ t: 'decline', ref });
        fail(ref, err);
      }
    },
    [holdOthers, patch, openMedia, rt, fail],
  );

  const decline = useCallback(
    (ref: string) => {
      rt.send({ t: 'decline', ref });
      finish(ref, 'Abgelehnt');
    },
    [rt, finish],
  );

  const hangup = useCallback(
    (ref: string) => {
      rt.send({ t: 'hangup', ref });
      finish(ref, 'Gespräch beendet');
    },
    [rt, finish],
  );

  const toggleHold = useCallback(
    (ref: string) => {
      const call = callsRef.current.find((c) => c.ref === ref);
      if (!call || call.state !== 'active') return;
      const held = !call.held;
      if (!held) holdOthers(ref);
      rt.send({ t: 'hold', ref, held });
      res.current.get(ref)?.stream.getAudioTracks().forEach((t) => (t.enabled = !held && !call.muted));
      patch(ref, { held });
    },
    [rt, patch, holdOthers],
  );

  const toggleMute = useCallback(
    (ref: string) => {
      const call = callsRef.current.find((c) => c.ref === ref);
      if (!call) return;
      const muted = !call.muted;
      res.current.get(ref)?.stream.getAudioTracks().forEach((t) => (t.enabled = !muted && !call.held));
      patch(ref, { muted });
    },
    [patch],
  );

  const sendDtmf = useCallback((ref: string, digits: string) => void rt.send({ t: 'dtmf', ref, digits }), [rt]);
  const transfer = useCallback((ref: string, target: string) => void rt.send({ t: 'transfer', ref, target }), [rt]);
  const transferTo = useCallback((ref: string, withRef: string) => void rt.send({ t: 'attended', ref, withRef }), [rt]);

  const setEnabled = useCallback((on: boolean) => {
    setEnabledState(on);
    try {
      localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
    } catch {
      /* ohne Speicher geht es auch */
    }
    if (on && 'Notification' in window && Notification.permission === 'default') void Notification.requestPermission();
  }, []);

  const value = useMemo<Softphone>(
    () => ({ status, extension, error, enabled, setEnabled, calls, dial, accept, decline, hangup, toggleHold, toggleMute, sendDtmf, transfer, transferTo }),
    [status, extension, error, enabled, setEnabled, calls, dial, accept, decline, hangup, toggleHold, toggleMute, sendDtmf, transfer, transferTo],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSoftphone(): Softphone {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('SoftphoneProvider fehlt');
  return ctx;
}
